import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL_HUBS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "monitoring", label: "Monitoring Centre" },
  { key: "innovation", label: "Innovation Centre" },
  { key: "hr", label: "Human Centre (HR)" },
  { key: "learning", label: "Learning Hub" },
  { key: "comms", label: "Comms Centre" },
  { key: "knowledge", label: "Knowledge Hub" },
  { key: "agent", label: "Agent Support" },
  { key: "smart_office", label: "Smart Office" },
  { key: "aipps", label: "Aipps Credits" },
  { key: "alerts", label: "Alerts Hub" },
  { key: "rewards", label: "Rewards Centre" },
  { key: "documents", label: "Documents" },
  { key: "courses", label: "Courses" },
  { key: "logistics", label: "Logistics" },
  { key: "travelling", label: "Travelling Admin" },
  { key: "request_service", label: "Request Service" },
  { key: "vetting", label: "Smart Vetting" },
  { key: "cyber_monitor", label: "Cyber Monitor" },
  { key: "evet", label: "eVET Hub" },
  { key: "smartpay", label: "SmartPay" },
  { key: "erp", label: "ERP Hub" },
  { key: "ims", label: "IMS Hub" },
];

export default function PlanForm({ initialData, onSave, onCancel, saving }) {
  const [planType, setPlanType] = useState(initialData?.plan_type || "PERSONAL");
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [billingPeriod, setBillingPeriod] = useState(initialData?.billing_period || "monthly");
  const [priceMonthly, setPriceMonthly] = useState(initialData?.price_monthly || 0);
  const [priceYearly, setPriceYearly] = useState(initialData?.price_yearly || 0);
  const [pricePerUser, setPricePerUser] = useState(initialData?.price_per_user_monthly || 0);
  const [hubs, setHubs] = useState(initialData?.hubs || {});
  const [isPublic, setIsPublic] = useState(initialData?.is_public !== false);
  const [maxTeamMembers, setMaxTeamMembers] = useState(initialData?.features?.max_team_members || "");
  const [maxStorageGb, setMaxStorageGb] = useState(initialData?.features?.max_storage_gb || 5);
  const [apiAccess, setApiAccess] = useState(initialData?.features?.api_access || false);
  const [prioritySupport, setPrioritySupport] = useState(initialData?.features?.priority_support || false);

  const toggleHub = (key) => {
    setHubs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      plan_type: planType,
      name,
      description,
      billing_period: billingPeriod,
      price_monthly: priceMonthly,
      price_yearly: priceYearly,
      price_per_user_monthly: pricePerUser,
      hubs,
      is_public: isPublic,
      is_active: true,
      features: {
        max_hubs: Object.values(hubs).filter(Boolean).length,
        max_storage_gb: maxStorageGb,
        max_team_members: maxTeamMembers ? parseInt(maxTeamMembers) : null,
        api_access: apiAccess,
        priority_support: prioritySupport,
        custom_branding: false,
      },
      sort_order: initialData?.sort_order || 99,
    });
  };

  const selectedCount = Object.values(hubs).filter(Boolean).length;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Plan Type</Label>
          <Select value={planType} onValueChange={setPlanType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PERSONAL">Personal</SelectItem>
              <SelectItem value="CORPORATE">Corporate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Billing Period</Label>
          <Select value={billingPeriod} onValueChange={setBillingPeriod}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Free / No billing</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
              <SelectItem value="per_user">Per User</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Plan Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Corporate Monthly" />
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description for pricing page" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Monthly Price (cents)</Label>
          <Input type="number" value={priceMonthly} onChange={(e) => setPriceMonthly(parseInt(e.target.value) || 0)} />
        </div>
        <div className="space-y-2">
          <Label>Yearly Price (cents)</Label>
          <Input type="number" value={priceYearly} onChange={(e) => setPriceYearly(parseInt(e.target.value) || 0)} />
        </div>
        <div className="space-y-2">
          <Label>Per User Price (cents/mo)</Label>
          <Input type="number" value={pricePerUser} onChange={(e) => setPricePerUser(parseInt(e.target.value) || 0)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Max Team Members</Label>
          <Input type="number" value={maxTeamMembers} onChange={(e) => setMaxTeamMembers(e.target.value)} placeholder="Unlimited if empty" />
        </div>
        <div className="space-y-2">
          <Label>Storage (GB)</Label>
          <Input type="number" value={maxStorageGb} onChange={(e) => setMaxStorageGb(parseInt(e.target.value) || 0)} />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch checked={isPublic} onCheckedChange={setIsPublic} id="is-public" />
          <Label htmlFor="is-public">Show on pricing page</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={apiAccess} onCheckedChange={setApiAccess} id="api-access" />
          <Label htmlFor="api-access">API Access</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={prioritySupport} onCheckedChange={setPrioritySupport} id="priority-support" />
          <Label htmlFor="priority-support">Priority Support</Label>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Included Hubs ({selectedCount} of {ALL_HUBS.length} selected)</Label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {ALL_HUBS.map((hub) => (
            <div key={hub.key} className="flex items-center gap-2 p-2 rounded border">
              <Switch
                checked={hubs[hub.key] !== false}
                onCheckedChange={() => toggleHub(hub.key)}
                id={`hub-${hub.key}`}
              />
              <Label htmlFor={`hub-${hub.key}`} className="text-sm cursor-pointer">{hub.label}</Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        )}
        <Button type="submit" disabled={saving || !name}>
          {saving ? "Saving..." : initialData ? "Update Plan" : "Create Plan"}
        </Button>
      </div>
    </form>
  );
}
